import * as React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { ChevronRight, LocalOfferOutlined, PercentOutlined } from '@mui/icons-material';
import { useResourceDefinitions } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../../components/page-header/PageHeader';
import { PAGE_GUTTER } from '../../core/theme/layout';

/**
 * Ekran pod pozycja "Rabaty" w menu. Rabaty automatyczne (`discounts`) i kody
 * rabatowe (`coupons`) zostaja osobnymi zasobami z wlasnymi listami i formularzami
 * — tutaj uzytkownik wybiera, ktorymi chce sie zajac.
 */
type DiscountKind = 'discounts' | 'coupons';

// Kolejnosc = kolejnosc kafelkow.
const DISCOUNT_KINDS: { key: DiscountKind; icon: React.ReactElement }[] = [
  { key: 'discounts', icon: <PercentOutlined /> },
  { key: 'coupons', icon: <LocalOfferOutlined /> },
];

export const DiscountKindsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const resources = useResourceDefinitions();

  // Kafelek tylko dla zasobu zarejestrowanego w App.tsx (czesc zalezy od roli).
  const kinds = DISCOUNT_KINDS.filter((kind) => resources[kind.key]);

  return (
    <>
      <PageHeader title={t('resources.discounts')} subtitle={t('discountsSection.subtitle')} />
      <Box
        sx={{
          px: PAGE_GUTTER,
          pb: 'var(--space-8)',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
          gap: 'var(--space-4)',
          maxWidth: 960,
        }}
      >
        {kinds.map((kind) => (
          <ButtonBase
            key={kind.key}
            onClick={() => navigate(`/${kind.key}`)}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              gap: 'var(--space-4)',
              p: 'var(--space-5)',
              textAlign: 'left',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-lg)',
              backgroundColor: 'var(--color-surface)',
              transition: 'border-color 120ms, background-color 120ms',
              '&:hover, &.Mui-focusVisible': {
                borderColor: 'var(--color-primary)',
                backgroundColor: 'var(--color-primary-soft)',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                width: 48,
                height: 48,
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--color-primary-soft)',
                color: 'var(--color-primary)',
              }}
            >
              {kind.icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 'var(--text-base)' }}>
                {t(`discountsSection.kinds.${kind.key}.title`)}
              </Typography>
              <Typography
                sx={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-text-secondary)',
                  mt: 'var(--space-1)',
                  lineHeight: 'var(--leading-normal)',
                }}
              >
                {t(`discountsSection.kinds.${kind.key}.description`)}
              </Typography>
            </Box>
            <ChevronRight sx={{ alignSelf: 'center', color: 'var(--color-text-muted)' }} />
          </ButtonBase>
        ))}
      </Box>
    </>
  );
};

export default DiscountKindsPage;
